import { styled } from '~/themes';
import NoImage from '~/assets/images/icons/round-no-image.svg';
import UploadIcon from '~/assets/images/icons/upload-gray.svg';
import { Skeleton, Stack, Typography } from '@mui/material';
import Button from '~/components/common/Button';
import { useUploadImageContext } from './UploadImageProvider';

type UploadImageSelectionProps = {
  loading?: boolean;
  rounded?: boolean;
  size?: number;
  hasIconUpload?: boolean;
};

const StyledImageContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'rounded',
})<{ rounded: boolean; size: number }>(({ theme, rounded, size }) => ({
  'position': 'relative',
  'width': `${size}px`,
  'height': `${size}px`,
  'borderRadius': rounded ? '50%' : theme.spacing(0.5),
  'overflow': rounded ? 'hidden' : 'unset',
  '&:hover': {
    '& > div': {
      display: 'flex',
    },
  },
}));

const StyledImageEdit = styled('div')(({ theme }) => ({
  borderRadius: theme.spacing(0.5),
  background: 'rgba(0,0,0,.5)',
  color: theme.color.white,
  display: 'none',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  position: 'absolute',
  bottom: 0,
  top: 0,
  left: 0,
  right: 0,
}));

const StyledImage = styled('img', {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size: number }>(({ theme, size }) => ({
  width: `${size}px`,
  height: `${size}px`,
  borderRadius: theme.spacing(0.5),
  objectFit: 'contain',
}));

const StyledSkeletonLoading = styled(Skeleton)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  top: 0,
  left: 0,
  right: 0,
  width: '100%',
  height: '100%',
  borderRadius: theme.spacing(0.5),
}));

export const UploadImageSelection: React.FC<UploadImageSelectionProps> = ({
  loading = false,
  rounded = false,
  size = 60,
  hasIconUpload = false,
}) => {
  const { defaultImages, onToggleUploadDialog } = useUploadImageContext();
  if (hasIconUpload) {
    return (
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '8px',
          alignItems: 'center',
        }}
      >
        <StyledImageContainer rounded={rounded} size={size}>
          {loading ? (
            <StyledSkeletonLoading variant='rectangular' />
          ) : (
            <StyledImage
              src={defaultImages[0] || NoImage}
              alt='avatar'
              size={size}
            />
          )}
        </StyledImageContainer>
        <Button
          buttonType='text'
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '4px',
          }}
          onClick={onToggleUploadDialog}
        >
          <img src={UploadIcon} alt='upload-icon' />
          <Typography sx={{ fontWeight: 500 }}>Upload image</Typography>
        </Button>
      </Stack>
    );
  }

  return (
    <StyledImageContainer rounded={rounded} size={size}>
      {loading ? (
        <StyledSkeletonLoading variant='rectangular' />
      ) : (
        <StyledImage
          src={defaultImages[0] || NoImage}
          alt='avatar'
          size={size}
        />
      )}
      <StyledImageEdit
        role='button'
        tabIndex={0}
        onClick={onToggleUploadDialog}
      >
        Edit
      </StyledImageEdit>
    </StyledImageContainer>
  );
};
